"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Image, { StaticImageData } from "next/image";
import { usePathname } from "next/navigation";
import {
  Shield,
  Lock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
} from "lucide-react";
import landingPageData from "@data/landing-pages/agentic-bi-tool.json";
import demoFormData from "@data/components/demo-form.json";
import { trackGoogleAdsConversion } from "@utils/ga4Tracking";
import {
  GOOGLE_ADS_CONVERSION_ID,
  GOOGLE_ADS_CONVERSION_LABELS,
} from "@utils/googleAdsConversions";

interface DemoFormData {
  company: string;
  name: string;
  email: string;
  phone: string;
  employees: string;
  currentBITool: string;
  challenges: string[];
  message: string;
  gdprConsent: boolean;
}

interface DemoFormProps {
  onClose: () => void;
  /* Prefills "Nachricht" — e.g. the price calculator hands over the
     configuration the visitor just assembled, so the lead arrives with the
     Sheet-/Spalten-Setup instead of an empty message. */
  initialMessage?: string;
  mapTeamPreview?: {
    locationTitle: string;
    employeeCount: number;
    members: {
      id: string;
      name: string;
      role: string;
      alt: string;
      image: StaticImageData;
    }[];
  };
}

interface FormOption {
  value: string;
  label: string;
}

// Separate interface for touched state
interface TouchedState {
  company?: boolean;
  name?: boolean;
  email?: boolean;
  phone?: boolean;
  employees?: boolean;
  currentBITool?: boolean;
  challenges?: boolean;
  message?: boolean;
  gdprConsent?: boolean;
}

// Separate interface for errors state
interface ErrorState {
  company?: string;
  name?: string;
  email?: string;
  phone?: string;
  employees?: string;
  currentBITool?: string;
  challenges?: string;
  message?: string;
  gdprConsent?: string;
}

const DemoForm: React.FC<DemoFormProps> = ({
  onClose,
  initialMessage,
  mapTeamPreview,
}) => {
  const pathname = usePathname();
  const resolvedSource = pathname
    ? `Neue Registrierung über ${pathname}`
    : "Neue Registrierung über Webseite DBI";

  // Form State
  const [formData, setFormData] = useState<DemoFormData>({
    company: "",
    name: "",
    email: "",
    phone: "",
    employees: "",
    currentBITool: "",
    challenges: [],
    message: initialMessage ?? "",
    gdprConsent: false,
  });

  // UI State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<ErrorState>({});
  const [touched, setTouched] = useState<TouchedState>({});
  const [showSuccess, setShowSuccess] = useState(false);

  // Autocomplete State
  const [companySuggestions, setCompanySuggestions] = useState<string[]>([]);
  const [showCompanySuggestions, setShowCompanySuggestions] = useState(false);
  const [bitoolSuggestions, setBitoolSuggestions] = useState<string[]>([]);
  const [showBitoolSuggestions, setShowBitoolSuggestions] = useState(false);

  // Refs
  const companyInputRef = useRef<HTMLInputElement>(null);
  const bitoolInputRef = useRef<HTMLInputElement>(null);

  // Check if form has data
  const hasFormData = useCallback(() => {
    return (
      formData.company.trim() !== "" ||
      formData.name.trim() !== "" ||
      formData.email.trim() !== "" ||
      formData.phone.trim() !== "" ||
      formData.employees !== "" ||
      formData.currentBITool.trim() !== "" ||
      formData.challenges.length > 0 ||
      /* A prefilled message is not something the visitor typed, so it must not
         trigger the "Daten gehen verloren" prompt on close. */
      formData.message.trim() !== (initialMessage ?? "").trim() ||
      formData.gdprConsent
    );
  }, [formData, initialMessage]);

  // Safe close function with confirmation
  const handleSafeClose = useCallback(() => {
    if (hasFormData()) {
      const confirmed = window.confirm(
        "Möchten Sie das Formular wirklich schließen? Alle eingegebenen Daten gehen verloren."
      );
      if (confirmed) {
        onClose();
      }
    } else {
      onClose();
    }
  }, [hasFormData, onClose]);

  // Predefined data for autocomplete - loaded from JSON
  const companyDatabase = demoFormData.companyDatabase;
  const biToolsDatabase = demoFormData.biToolsDatabase;
  // Use challenges from centralized form config
  const challengeOptions = landingPageData.form.fields.challenges.options as FormOption[];

  // Validation Functions
  const validateField = (
    name: keyof DemoFormData,
    value: string | string[] | boolean
  ): string => {
    switch (name) {
      case "company":
        return (value as string).trim().length < 2
          ? "Firmenname muss mindestens 2 Zeichen haben"
          : "";
      case "name":
        return (value as string).trim().length < 2
          ? "Name muss mindestens 2 Zeichen haben"
          : "";
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return !emailRegex.test(value as string)
          ? "Gültige E-Mail-Adresse erforderlich"
          : "";
      case "phone":
        if ((value as string).trim()) {
          const phoneRegex = /^[\+]?[0-9\s\-\(\)]{10,}$/;
          return !phoneRegex.test(value as string)
            ? "Gültige Telefonnummer erforderlich"
            : "";
        }
        return "";
      case "employees":
        return !value ? "Unternehmensgröße erforderlich" : "";
      case "challenges":
        const challengesRequired = landingPageData?.form?.fields?.challenges?.required !== false;
        if (challengesRequired && (value as string[]).length === 0) {
          return "Mindestens eine Herausforderung auswählen";
        }
        return "";
      case "gdprConsent":
        return !value ? "DSGVO-Zustimmung erforderlich" : "";
      default:
        return "";
    }
  };

  const validateForm = (): boolean => {
    const newErrors: ErrorState = {};
    let isValid = true;

    const allTouched: TouchedState = {};
    Object.keys(formData).forEach((key) => {
      const fieldName = key as keyof DemoFormData;
      allTouched[fieldName] = true;
      const error = validateField(fieldName, formData[fieldName]);
      if (error) {
        newErrors[fieldName] = error;
        isValid = false;
      }
    });

    setTouched((prev) => ({ ...prev, ...allTouched }));
    setErrors(newErrors);
    return isValid;
  };

  // Autocomplete Functions
  const filterSuggestions = (input: string, database: string[]): string[] => {
    return database
      .filter((item) => item.toLowerCase().includes(input.toLowerCase()))
      .slice(0, 5);
  };

  const handleCompanyInput = (value: string) => {
    setFormData((prev) => ({ ...prev, company: value }));
    setTouched((prev) => ({ ...prev, company: true }));

    if (value.length >= 2) {
      const suggestions = filterSuggestions(value, companyDatabase);
      setCompanySuggestions(suggestions);
      setShowCompanySuggestions(true);
    } else {
      setShowCompanySuggestions(false);
    }
  };

  const handleBitoolInput = (value: string) => {
    setFormData((prev) => ({ ...prev, currentBITool: value }));

    if (value.length >= 2) {
      const suggestions = filterSuggestions(value, biToolsDatabase);
      setBitoolSuggestions(suggestions);
      setShowBitoolSuggestions(true);
    } else {
      setShowBitoolSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion: string, type: "company" | "bitool") => {
    if (type === "company") {
      setFormData((prev) => ({ ...prev, company: suggestion }));
      setShowCompanySuggestions(false);
      companyInputRef.current?.focus();
    } else {
      setFormData((prev) => ({ ...prev, currentBITool: suggestion }));
      setShowBitoolSuggestions(false);
      bitoolInputRef.current?.focus();
    }
  };

  // Form Handlers
  const handleInputChange = (
    name: keyof DemoFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));

    // Real-time validation for email field
    if (name === "email") {
      const emailValue = value as string;
      if (emailValue.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isValidEmail = emailRegex.test(emailValue);

        if (!isValidEmail) {
          setErrors((prev) => ({
            ...prev,
            [name]: "Gültige E-Mail-Adresse erforderlich",
          }));
        } else {
          setErrors((prev) => ({ ...prev, [name]: "" }));
        }
      } else {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    } else {
      // Clear error when user starts typing for other fields
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    }
  };

  const handleChallengeChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      challenges: prev.challenges.includes(value)
        ? prev.challenges.filter((c) => c !== value)
        : [...prev.challenges, value],
    }));
    setTouched((prev) => ({ ...prev, challenges: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.gdprConsent) {
      setErrors((prev) => ({
        ...prev,
        gdprConsent: "DSGVO-Zustimmung erforderlich",
      }));
      setTouched((prev) => ({ ...prev, gdprConsent: true }));
      return;
    }

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const webhookData = {
        company: formData.company.trim(),
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        employees: formData.employees,
        currentBITool: formData.currentBITool.trim(),
        challenges: formData.challenges,
        message: formData.message.trim(),
        gdprConsent: formData.gdprConsent,
        timestamp: new Date().toISOString(),
        source: resolvedSource,
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        formType: "demo_request",
        submissionId: `abis_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`,
      };

      const response = await fetch("/api/demo-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(webhookData),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${response.status}`);
      }

      setShowSuccess(true);

      // Google Ads conversion with Enhanced Conversions. `webhookData` is used
      // rather than `formData` because it is already trimmed/lowercased, and it
      // is captured before the reset below clears the fields.
      trackGoogleAdsConversion(
        GOOGLE_ADS_CONVERSION_ID,
        GOOGLE_ADS_CONVERSION_LABELS.lead,
        "abis-demo-form",
        {
          email: webhookData.email,
          phone: webhookData.phone,
          name: webhookData.name,
        }
      );

      setTimeout(() => {
        onClose();
        setShowSuccess(false);
        setFormData({
          company: "",
          name: "",
          email: "",
          phone: "",
          employees: "",
          currentBITool: "",
          challenges: [],
          message: "",
          gdprConsent: false,
        });
      }, 2000);
    } catch (error) {
      console.error("❌ Form submission error:", error);
      setErrors({
        message: `Ein Fehler ist aufgetreten: ${
          error instanceof Error ? error.message : "Unbekannter Fehler"
        }. Bitte versuchen Sie es erneut.`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        companyInputRef.current &&
        !companyInputRef.current.contains(event.target as Node)
      ) {
        setShowCompanySuggestions(false);
      }
      if (
        bitoolInputRef.current &&
        !bitoolInputRef.current.contains(event.target as Node)
      ) {
        setShowBitoolSuggestions(false);
      }
    };

    // Handle Escape key for safe close
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleSafeClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [handleSafeClose]); // Use handleSafeClose as dependency

  if (showSuccess) {
    return (
      <div className="flex flex-col items-center justify-center p-6 sm:p-8 text-center">
        <CheckCircle className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 mb-3 sm:mb-4" />
        <h3 className="mb-2 text-lg sm:text-xl">{landingPageData?.form?.successMessage?.title}</h3>
        <p className="text-gray-600 text-sm sm:text-base">
          {landingPageData?.form?.successMessage?.message}
        </p>
      </div>
    );
  }

  const inputBase = "w-full px-3 sm:px-4 py-3 !rounded-none bg-gray-50 border-0 border-b-2 border-gray-200 focus:border-abis-primary focus:bg-white focus:ring-0 focus:outline-none transition-all duration-200 text-base sm:text-sm placeholder:text-gray-400 outline-none [&]:rounded-none";
  const inputError = "border-red-400 bg-red-50/30";
  const labelBase = "block text-[11px] sm:text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1";

  return (
    <div className="px-1 sm:px-0">
      <p className="text-center mb-4 sm:mb-6 text-gray-500 text-xs sm:text-sm leading-relaxed">
        {landingPageData?.form?.subtitle}
      </p>
      {mapTeamPreview ? (
        <div className="mb-4 rounded-xl border border-abis-primary/15 bg-abis-primary/5 p-4">
          <p className="text-sm font-semibold text-abis-primary">
            Team vor Ort: {mapTeamPreview.locationTitle.split(" (")[0]}
          </p>
          <div className="mt-3 flex items-center">
            {mapTeamPreview.members.map((member, index) => (
              <div
                key={member.id}
                className="-ml-3 first:ml-0 relative h-12 w-12 overflow-hidden rounded-full border-2 border-white bg-white"
                style={{ zIndex: mapTeamPreview.members.length - index + 1 }}
                title={`${member.name} - ${member.role}`}
                aria-label={member.name}
              >
                <Image
                  src={member.image}
                  alt={member.alt}
                  fill
                  className="object-cover"
                  sizes="48px"
                />
              </div>
            ))}
            <div
              className={`relative z-0 flex h-12 w-12 items-center justify-center rounded-full border-2 border-white bg-primary text-xs font-bold text-white ${
                mapTeamPreview.members.length > 0 ? "-ml-3" : "ml-0"
              }`}
            >
              +{mapTeamPreview.employeeCount}
            </div>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-5"
        role="form"
        aria-label={landingPageData?.form?.title}
        noValidate
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div className="relative">
            <label htmlFor="company" className={labelBase}>
              {landingPageData?.form?.fields?.company?.label} *
            </label>
            <input
              ref={companyInputRef}
              type="text"
              id="company"
              name="company"
              autoComplete="organization"
              value={formData.company}
              onChange={(e) => handleCompanyInput(e.target.value)}
              placeholder={landingPageData?.form?.fields?.company?.placeholder}
              className={`${inputBase} ${errors.company && touched.company ? inputError : ""}`}
              aria-describedby={errors.company && touched.company ? "company-error" : undefined}
              aria-invalid={!!(errors.company && touched.company)}
              required
            />
            {showCompanySuggestions && companySuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden">
                {companySuggestions.map((suggestion, index) => (
                  <button key={index} type="button" onClick={() => selectSuggestion(suggestion, "company")}
                    className="w-full text-left px-3 py-3 sm:px-4 sm:py-2.5 hover:bg-abis-primary/5 text-sm transition-colors active:bg-abis-primary/10"
                  >{suggestion}</button>
                ))}
              </div>
            )}
            {errors.company && touched.company && (
              <p id="company-error" className="text-red-500 text-xs mt-1 flex items-center" role="alert" aria-live="polite">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" aria-hidden="true" />{errors.company}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="name" className={labelBase}>
              {landingPageData?.form?.fields?.name?.label} *
            </label>
            <input
              type="text" id="name" name="name" autoComplete="name"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder={landingPageData?.form?.fields?.name?.placeholder}
              className={`${inputBase} ${errors.name && touched.name ? inputError : ""}`}
              aria-describedby={errors.name && touched.name ? "name-error" : undefined}
              aria-invalid={!!(errors.name && touched.name)}
              required
            />
            {errors.name && touched.name && (
              <p id="name-error" className="text-red-500 text-xs mt-1 flex items-center" role="alert" aria-live="polite">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" aria-hidden="true" />{errors.name}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label htmlFor="email" className={labelBase}>
              {landingPageData?.form?.fields?.email?.label} *
            </label>
            <input
              type="email" id="email" name="email" autoComplete="email"
              inputMode="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              onBlur={() => {
                const emailError = validateField("email", formData.email);
                if (emailError) setErrors((prev) => ({ ...prev, email: emailError }));
              }}
              placeholder={landingPageData?.form?.fields?.email?.placeholder}
              className={`${inputBase} ${errors.email && touched.email ? inputError : ""}`}
              aria-describedby={errors.email && touched.email ? "email-error" : undefined}
              aria-invalid={!!(errors.email && touched.email)}
              required
            />
            {errors.email && touched.email && (
              <p id="email-error" className="text-red-500 text-xs mt-1 flex items-center" role="alert" aria-live="polite">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" aria-hidden="true" />{errors.email}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="phone" className={labelBase}>
              {landingPageData?.form?.fields?.phone?.label}
            </label>
            <input
              type="tel" id="phone" name="phone" autoComplete="tel"
              inputMode="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange("phone", e.target.value)}
              placeholder={landingPageData?.form?.fields?.phone?.placeholder}
              className={`${inputBase} ${errors.phone && touched.phone ? inputError : ""}`}
              aria-describedby={errors.phone && touched.phone ? "phone-error" : undefined}
              aria-invalid={!!(errors.phone && touched.phone)}
            />
            {errors.phone && touched.phone && (
              <p id="phone-error" className="text-red-500 text-xs mt-1 flex items-center" role="alert" aria-live="polite">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" aria-hidden="true" />{errors.phone}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label htmlFor="employees" className={labelBase}>
              {landingPageData?.form?.fields?.employees?.label} *
            </label>
            <select
              id="employees" name="employees" autoComplete="off"
              value={formData.employees}
              onChange={(e) => handleInputChange("employees", e.target.value)}
              className={`${inputBase} appearance-none cursor-pointer ${errors.employees && touched.employees ? inputError : ""}`}
            >
              <option value="">Bitte wählen</option>
              {landingPageData?.form?.fields?.employees?.options?.map(
                (option: FormOption) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                )
              )}
            </select>
            {errors.employees && touched.employees && (
              <p className="text-red-500 text-xs mt-1 flex items-center">
                <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />{errors.employees}
              </p>
            )}
          </div>

          <div className="relative">
            <label htmlFor="currentBITool" className={labelBase}>
              {landingPageData?.form?.fields?.currentBITool?.label}
            </label>
            <input
              ref={bitoolInputRef}
              type="text" id="currentBITool" name="currentBITool" autoComplete="off"
              value={formData.currentBITool}
              onChange={(e) => handleBitoolInput(e.target.value)}
              placeholder={landingPageData?.form?.fields?.currentBITool?.placeholder}
              className={inputBase}
            />
            {showBitoolSuggestions && bitoolSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-100 rounded-lg shadow-xl overflow-hidden">
                {bitoolSuggestions.map((suggestion, index) => (
                  <button key={index} type="button" onClick={() => selectSuggestion(suggestion, "bitool")}
                    className="w-full text-left px-3 py-3 sm:px-4 sm:py-2.5 hover:bg-abis-primary/5 text-sm transition-colors active:bg-abis-primary/10"
                  >{suggestion}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className={labelBase}>
            {landingPageData?.form?.fields?.challenges?.label} *
          </label>
          <div className="flex flex-wrap gap-2">
            {challengeOptions.map((option: FormOption) => (
              <label key={option.value} className="cursor-pointer">
                <input
                  type="checkbox" name="challenges" value={option.value}
                  checked={formData.challenges.includes(option.value)}
                  onChange={(e) => handleChallengeChange(e.target.value)}
                  className="sr-only"
                />
                <span className={`inline-flex items-center gap-1.5 px-3 sm:px-3.5 py-2 sm:py-1.5 rounded-full text-sm font-medium transition-all duration-200 border select-none ${
                  formData.challenges.includes(option.value)
                    ? "bg-abis-primary text-white border-abis-primary shadow-sm"
                    : "bg-gray-50 text-gray-600 border-gray-200 hover:border-abis-primary/40 hover:text-abis-primary active:bg-abis-primary/5"
                }`}>
                  {formData.challenges.includes(option.value) && (
                    <CheckCircle className="w-3.5 h-3.5" />
                  )}
                  {option.label}
                </span>
              </label>
            ))}
          </div>
          {errors.challenges && touched.challenges && (
            <p className="text-red-500 text-xs mt-1.5 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />{errors.challenges}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="message" className={labelBase}>
            {landingPageData?.form?.fields?.message?.label}
          </label>
          <textarea
            id="message" name="message" autoComplete="off"
            value={formData.message}
            onChange={(e) => handleInputChange("message", e.target.value)}
            rows={2} maxLength={600}
            placeholder={landingPageData?.form?.fields?.message?.placeholder}
            className={`${inputBase} resize-none`}
          />
        </div>

        <label htmlFor="gdprConsent" className="flex items-start gap-3 cursor-pointer group py-2 sm:py-1 -mx-1 px-1">
          <input
            type="checkbox" id="gdprConsent" name="gdprConsent"
            checked={formData.gdprConsent}
            onChange={(e) => handleInputChange("gdprConsent", e.target.checked)}
            className="sr-only"
          />
          <div className={`mt-0.5 w-6 h-6 sm:w-5 sm:h-5 rounded flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
            formData.gdprConsent
              ? "bg-abis-primary"
              : "bg-gray-100 border-2 border-gray-300 group-hover:border-abis-primary/50"
          }`}>
            {formData.gdprConsent && <CheckCircle className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-white" />}
          </div>
          <span className="text-xs text-gray-500 leading-relaxed group-hover:text-gray-700 transition-colors">
            {landingPageData?.form?.fields?.gdprConsent?.label}{" "}
            <a href={landingPageData?.form?.fields?.gdprConsent?.linkUrl}
              className="text-abis-primary hover:underline" target="_blank" rel="noopener noreferrer"
            >{landingPageData?.form?.fields?.gdprConsent?.linkText}</a>*
          </span>
        </label>
        {errors.gdprConsent && touched.gdprConsent && (
          <p className="text-red-500 text-xs flex items-center">
            <AlertCircle className="w-3 h-3 mr-1 flex-shrink-0" />{errors.gdprConsent}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !formData.gdprConsent}
          className={`w-full py-3.5 sm:py-3 rounded-xl font-semibold text-base sm:text-sm transition-all duration-200 relative focus:outline-none focus:ring-4 focus:ring-abis-primary/30 ${
            isSubmitting || !formData.gdprConsent
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-abis-primary text-white hover:bg-abis-primary/90 hover:shadow-lg active:scale-[0.98]"
          }`}
          aria-describedby={!formData.gdprConsent ? "gdpr-required" : undefined}
          aria-disabled={!!(isSubmitting || !formData.gdprConsent)}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              {landingPageData?.form?.loadingText}
            </span>
          ) : (
            <>
              {landingPageData?.form?.submitText}
              {!formData.gdprConsent && (
                <span id="gdpr-required" className="block text-[10px] font-normal mt-0.5 opacity-60" role="alert" aria-live="polite">
                  DSGVO-Zustimmung erforderlich
                </span>
              )}
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-4 sm:gap-6 pt-1" role="group" aria-label="Vertrauensindikatoren">
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-500" aria-hidden="true" />
            <span className="text-[10px] sm:text-[11px] text-gray-400 font-medium">DSGVO-konform</span>
          </div>
          <div className="flex items-center gap-1">
            <Lock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-500" aria-hidden="true" />
            <span className="text-[10px] sm:text-[11px] text-gray-400 font-medium">Vertraulich</span>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-500" aria-hidden="true" />
            <span className="text-[10px] sm:text-[11px] text-gray-400 font-medium">24h Antwort</span>
          </div>
        </div>
      </form>
    </div>
  );
};

export default DemoForm;
